// SegmentedControl — labelled segmented button group.
//
// Pick-one-of-N control rendered as a recessed pill container holding
// flat segment buttons. The active segment is filled teal with inverse
// text; inactive segments are transparent muted text that lift to
// canvas on hover. Stays in sync with the tokenized look used across
// the BeneCRM v2 surfaces (Playbook editor's Priority / Complexity row,
// Commitment modal's Priority / Complexity row).
//
// Accessibility: each segment is a button with ``aria-pressed`` so
// assistive tech announces the active state without forcing the
// caller into the heavier radiogroup/radio wiring.

import type { ReactNode } from "react";

import { cn } from "./cn";


export interface SegmentedControlOption {
  value: string;
  label: ReactNode;
}


export interface SegmentedControlProps {
  label: string;
  value: string;
  options: SegmentedControlOption[];
  onChange: (value: string) => void;
  /** Optional id prefix for the segment buttons.
   * When provided, each button gets ``data-testid={`${testIdPrefix}-${option.value}`}``. */
  testIdPrefix?: string;
  className?: string;
}


export function SegmentedControl({
  label, value, options, onChange, testIdPrefix, className,
}: SegmentedControlProps) {
  return (
    <fieldset className={cn("flex flex-col gap-2", className)}>
      <legend className="text-sm text-text-primary">{label}</legend>
      <div
        data-testid={testIdPrefix ? `${testIdPrefix}-pills` : undefined}
        className="inline-flex w-fit rounded-md border border-border-subtle bg-recessed p-1"
      >
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              data-testid={
                testIdPrefix ? `${testIdPrefix}-${option.value}` : undefined
              }
              onClick={() => onChange(option.value)}
              className={
                active
                  ? "rounded px-3 py-1.5 text-sm font-medium bg-teal text-text-inverse"
                  : "rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-canvas"
              }
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
